import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Table, Input, Button, Select, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { l303Api, ReportItem } from '../../services/l303Api';
import './L303Form.css';

// ---- Filter state ----
interface FilterState {
  docNoFrom: string;
  docNoTo: string;
  docType: number;
  categoryCode: string;
}

const emptyFilter = (): FilterState => ({
  docNoFrom: '',
  docNoTo: '',
  docType: 0,
  categoryCode: '',
});

const docTypeOptions = [
  { value: 0, label: '全部' },
  { value: 1, label: '品質手冊' },
  { value: 2, label: '作業程序書' },
  { value: 3, label: '作業指導書' },
];

// ---- Table columns ----
const columns: ColumnsType<ReportItem> = [
  { title: '文件編號', dataIndex: 'fl030_01', key: 'fl030_01', width: 110, fixed: 'left' },
  { title: '文件名稱', dataIndex: 'fl030_11', key: 'fl030_11', width: 200, ellipsis: true },
  {
    title: '制訂日期',
    dataIndex: 'fl030_12',
    key: 'fl030_12',
    width: 100,
    render: (v: string | null) => (v ? v.substring(0, 10) : ''),
  },
  {
    title: '修訂日期',
    dataIndex: 'fl030_13',
    key: 'fl030_13',
    width: 100,
    render: (v: string | null) => (v ? v.substring(0, 10) : ''),
  },
  { title: '代碼', dataIndex: 'fl030_14', key: 'fl030_14', width: 60 },
  { title: '版次', dataIndex: 'fl030_15', key: 'fl030_15', width: 60, align: 'right' },
  {
    title: '文件類型',
    dataIndex: 'fl030_16',
    key: 'fl030_16',
    width: 100,
    render: (v: number) => {
      const map: Record<number, string> = { 1: '品質手冊', 2: '作業程序書', 3: '作業指導書' };
      return map[v] || String(v);
    },
  },
  { title: '類別代碼', dataIndex: 'fl030_17', key: 'fl030_17', width: 90 },
  { title: '類別名稱', dataIndex: 'category_name', key: 'category_name', width: 120, ellipsis: true },
  { title: '狀態', dataIndex: 'fl030_80', key: 'fl030_80', width: 50 },
];

// ---- Component ----
const L303Form: React.FC = () => {
  const navigate = useNavigate();
  const [filter, setFilter] = useState<FilterState>(emptyFilter());
  const [rows, setRows] = useState<ReportItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [queried, setQueried] = useState(false);

  const setField = useCallback(
    <K extends keyof FilterState>(field: K, value: FilterState[K]) => {
      setFilter((prev) => ({ ...prev, [field]: value }));
    },
    []
  );

  const onQuery = useCallback(async () => {
    setLoading(true);
    setQueried(false);
    try {
      const res = await l303Api.getReport({
        docNoFrom: filter.docNoFrom,
        docNoTo: filter.docNoTo,
        docType: filter.docType,
        categoryCode: filter.categoryCode,
      });
      setRows(res.items || []);
      setQueried(true);
      if (!res.items || res.items.length === 0) {
        message.info('No matching document records found');
      } else {
        message.success(`Found ${res.total} records`);
      }
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: string } } };
      message.error(err.response?.data?.error || 'Query failed');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') onQuery();
    },
    [onQuery]
  );

  const onClear = useCallback(() => {
    setFilter(emptyFilter());
    setRows([]);
    setQueried(false);
  }, []);

  return (
    <div className="l303-form">
      {loading && <div className="loading-overlay">Loading...</div>}

      <div className="page-wrapper">
        <div className="page-title-bar">
          <div className="page-title-left">
            <nav className="breadcrumb-inline">
              <a href="#/erp" onClick={(e) => { e.preventDefault(); navigate('/erp'); }}>Home</a>
              <span>/</span>
              <a href="#/erp" onClick={(e) => { e.preventDefault(); navigate('/erp'); }}>Document</a>
              <span>/</span>
              <span className="current">Document Listing</span>
            </nav>
            <span className="title-separator">&mdash;</span>
            <h1>Document Listing Report</h1>
            <span className="badge">L303</span>
          </div>
        </div>

        <div className="filter-panel">
          <div className="filter-row">
            <span className="filter-label">Doc No From:</span>
            <Input value={filter.docNoFrom} onChange={(e) => setField('docNoFrom', e.target.value)} onKeyDown={onKeyDown} placeholder="Start" style={{ width: 130 }} allowClear />
            <span className="filter-sep">To</span>
            <Input value={filter.docNoTo} onChange={(e) => setField('docNoTo', e.target.value)} onKeyDown={onKeyDown} placeholder="End" style={{ width: 130 }} allowClear />
            <span className="filter-label">Doc Type:</span>
            <Select
              value={filter.docType}
              onChange={(v) => setField('docType', v)}
              options={docTypeOptions}
              style={{ width: 140 }}
            />
          </div>

          <div className="filter-row">
            <span className="filter-label">Category:</span>
            <Input value={filter.categoryCode} onChange={(e) => setField('categoryCode', e.target.value)} onKeyDown={onKeyDown} placeholder="Category Code" style={{ width: 130 }} allowClear />
            <Button type="primary" onClick={onQuery} loading={loading}>Query</Button>
            <Button onClick={onClear}>Clear</Button>
          </div>
        </div>

        <div className="table-card">
          {queried && (
            <div className="table-header">
              <div className="info">Total: <strong>{rows.length}</strong> records</div>
            </div>
          )}
          {queried ? (
            <Table<ReportItem>
              columns={columns}
              dataSource={rows}
              rowKey={(r) => r.fl030_01}
              pagination={{ pageSize: 50, showSizeChanger: true, showTotal: (t) => `Total ${t}` }}
              size="small"
              scroll={{ x: 1100, y: 460 }}
              locale={{ emptyText: 'No data' }}
              loading={loading}
            />
          ) : (
            <div className="empty-state">Set filter criteria and click "Query"</div>
          )}
        </div>

        <div className="action-bar">
          <Button danger size="large" onClick={() => navigate('/erp')}>Exit</Button>
        </div>
      </div>
    </div>
  );
};

export default L303Form;
